import {
    AutocompleteChangeReason,
    AutocompleteCloseReason,
    AutocompleteRenderOptionState,
    Grid,
    Theme,
} from '@mui/material';
import { CheckmarkIcon } from '@synergy/theme';
import { ChipAutocomplete, PoolButton, PoolModal, PoolTypography, ValueOrNDash } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { FormikProps } from 'formik';
import { SyntheticEvent, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { padZeroLeft } from '@synergy/utils';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import * as SupplierDetailOverviewActions from '../store/supplier-detail-overview-actions';
import { SupplierDetailOverviewFormValues } from '../models/supplier-detail-overview-form-values';
import { WhitelistedCustomer } from '../models/whitelisted-customer';
import { supplierSelector } from '../store/supplier-detail-overview-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    highlight: {
        color: theme.palette.primary.main,
    },
    grid: {
        display: 'flex',

        '& svg': {
            fontSize: 20,
            marginRight: theme.spacing(),
        },
    },
    preLine: {
        whiteSpace: 'pre-line',
    },
}));

interface SupplierOverviewWhitelistProps {
    formikProps: FormikProps<SupplierDetailOverviewFormValues>;
}

export const SupplierOverviewWhitelist = (props: SupplierOverviewWhitelistProps) => {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const [showDeleteAllCustomersModal, setShowDeleteAllCustomersModal] = useState(false);

    const supplier = useAppSelector(supplierSelector);
    const whitelistedCustomers = useAppSelector(
        state => state.contactsAndOrganizations.suppliers.supplierDetail.overview.whitelistedCustomer.items
    );
    const { formikProps } = props;

    useEffect(() => {
        dispatch(SupplierDetailOverviewActions.fetchWhitelistedCustomers(new URLSearchParams()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplier?.id]);

    const renderOption = (customer: WhitelistedCustomer, state: AutocompleteRenderOptionState) => {
        const nameMatches = match(customer.name, state.inputValue);
        const nameParts = parse(customer.name, nameMatches);

        return (
            <>
                <div>
                    <PoolTypography>{padZeroLeft(customer.id, 5)}</PoolTypography>
                </div>
                <div style={{ flex: 1 }}>
                    {nameParts.map(namePart => (
                        <span className={namePart.highlight ? classes.highlight : ''}>{namePart.text}</span>
                    ))}
                </div>
                <div style={{ width: '30%' }}>
                    <ValueOrNDash value={customer.mainAddress?.city} />
                </div>
            </>
        );
    };

    const getOptionLabel = (customer: WhitelistedCustomer) => {
        return `${padZeroLeft(customer.id, 5)} ${customer.name} ${customer.mainAddress?.city ?? '&ndash;'}`;
    };

    const handleCustomerChange = (
        _event: SyntheticEvent<Element, Event>,
        customer: WhitelistedCustomer[],
        reason: AutocompleteChangeReason
    ) => {
        if (reason === 'clear') {
            setShowDeleteAllCustomersModal(true);
        } else {
            formikProps.setFieldValue('whitelistedCustomers', customer);
        }
    };

    const handleTextFieldChange = (_event: SyntheticEvent<Element, Event>, value: string) => {
        if (value) {
            dispatch(SupplierDetailOverviewActions.fetchWhitelistedCustomers(new URLSearchParams({ search: value })));
        }
    };

    const handleResetCustomers = (_event: SyntheticEvent<Element, Event>, reason: AutocompleteCloseReason) => {
        if (reason === 'blur') {
            dispatch(SupplierDetailOverviewActions.fetchWhitelistedCustomers(new URLSearchParams()));
        }
    };

    const options = () => {
        const whitelistedCustomerIds = formikProps.values.whitelistedCustomers?.map(
            whitelistedCustomer => whitelistedCustomer.id
        );

        return whitelistedCustomers
            .filter(customer => !whitelistedCustomerIds.includes(customer.id))
            .map<WhitelistedCustomer>(customer => ({
                id: customer.id,
                mainAddress: customer.mainAddress,
                name: customer.name,
                number: padZeroLeft(customer.id, 5),
                email: customer.email,
            }));
    };

    const handleDeleteAllCustomers = () => {
        formikProps.setFieldValue('whitelistedCustomers', []);
        setShowDeleteAllCustomersModal(false);
    };

    const actionChildren = [
        <PoolButton key="2" styleType="firstLevel" variant="text" onClick={() => setShowDeleteAllCustomersModal(false)}>
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <PoolButton key="1" styleType="firstLevel" color="secondary" onClick={() => handleDeleteAllCustomers()}>
            <FormattedMessage id="app.common.delete" />
        </PoolButton>,
    ];

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={6}>
                <PoolTypography
                    component="span"
                    typography="body1"
                    id="app.contactsAndOrganizations.detail.sftp.customerDetails"
                    className={classes.preLine}
                />
            </Grid>
            <Grid item={true} xs={8}>
                <ChipAutocomplete
                    noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                    options={options()}
                    getOptionLabel={getOptionLabel}
                    renderOption={renderOption}
                    getChipLabel={(customer: WhitelistedCustomer) => customer.name}
                    onChange={handleCustomerChange}
                    value={formikProps.values.whitelistedCustomers || []}
                    textfieldPlaceholder="app.common.addTag"
                    onTextFieldValueChange={handleTextFieldChange}
                    onClose={handleResetCustomers}
                />
            </Grid>

            {formikProps.values.whitelistedCustomers?.length === 0 && (
                <Grid item={true} xs={6} className={classes.grid}>
                    <CheckmarkIcon />
                    <PoolTypography typography="body2" id="app.contactsAndOrganizations.detail.allCustomers" />
                </Grid>
            )}

            <PoolModal
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.unlockCustomers" />}
                isVisible={showDeleteAllCustomersModal}
                handleClose={() => setShowDeleteAllCustomersModal(false)}
                actionChildren={actionChildren}
                hasModalTypography={true}
            >
                <PoolTypography id="app.contactsAndOrganizations.detail.unlockCustomersWarning" />
            </PoolModal>
        </Grid>
    );
};
